import { createTheme, type Theme } from '@mui/material';
import axios from 'axios';

const API_URL = import.meta.env.VITE_APP_API_DOMAIN || 'http://localhost:8080';

axios.defaults.baseURL = API_URL;

const config = {
  API_URL,
  AUTH_CLIENT_ID: '998208635335-u7oh4sa6ns2kl7paktrci53qu6adbali.apps.googleusercontent.com',
  INDEXED_DB_NAME: 'images',
  INDEXED_DB_VERSION: 1,
  INDEXED_DB_OBJECT_STORE_NAME: 'PdfImages',
} as const;

export const designTokens = {
  primary: { main: '#4B68FF', light: '#5c77ff', dark: '#3D5AFE' },
  black: '#000000',
  white: '#FFFFFF',
  slate: {
    50: '#F8FAFC',
    100: '#F1F5F9',
    150: '#eceff4',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
    700: '#334155',
    900: '#0F172A',
  },
  blueRibbon: {
    50: '#ECF4FF',
    100: '#DDEAFF',
    200: '#C2D8FF',
    400: '#7595FF',
    500: '#4B68FF',
    600: '#3646F5',
  },
} as const;

const theme: Theme = createTheme({
  typography: {
    fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
  },
  palette: {
    mode: 'light',
    primary: {
      main: designTokens.primary.main,
      light: designTokens.primary.light,
      dark: designTokens.primary.dark,
      contrastText: designTokens.white,
    },
    info: {
      main: designTokens.blueRibbon[500],
      contrastText: designTokens.white,
    },
    success: {
      main: '#00a336',
      contrastText: designTokens.white,
    },
    text: {
      primary: designTokens.slate[900],
      secondary: designTokens.slate[700],
      disabled: designTokens.slate[400],
    },
    background: {
      default: designTokens.slate[50],
      paper: designTokens.slate[50],
    },
    divider: designTokens.slate[200],
  },
  spacing: 4,
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#2A35D8',
          cursor: 'pointer',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardInfo: {
          backgroundColor: '#ecf4ff',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          '&.Mui-disabled': {
            color: '#CBD5E1',
            borderColor: '#E2E8F0',
          },
        },

        contained: {
          '&.MuiButton-disableElevation': {
            boxShadow: `0px 1px 2px 0px #1018280D`,
            '&:hover': {
              boxShadow: `0px 1px 2px 0px #1018280D`,
            },
          },
          '&:not(.MuiButton-disableElevation)': {
            boxShadow: `0px 2px 5px 0px #4B68FF1A,
                      0px 9px 9px 0px #4B68FF17,
                      0px 21px 12px 0px #4B68FF0D,
                      0px 37px 15px 0px #4B68FF03,
                      0px 57px 16px 0px #4B68FF00`,
          },
          '&:hover': {
            backgroundColor: '#3646F5',
            boxShadow: `0px 2px 5px 0px #4B68FF1A,
                      0px 9px 9px 0px #4B68FF17,
                      0px 21px 12px 0px #4B68FF0D,
                      0px 37px 15px 0px #4B68FF03,
                      0px 57px 16px 0px #4B68FF00`,
          },
        },

        outlined: {
          backgroundColor: '#ecf4ff',
          borderColor: '#c2d8ff',
          '&:hover': {
            backgroundColor: '#DDEAFF',
            borderColor: '#C2D8FF',
            color: '#3646F5',
          },
          '&.Mui-focusVisible': {
            boxShadow: '0px 0px 0px 4px #94A3B824, 0px 1px 2px 0px #1018280D',
          },
          '&.Mui-disabled': {
            backgroundColor: '#ffffff',
          },
          '&.buttonSecondary': {
            borderColor: '#CBD5E1',
            color: '#334155',
            backgroundColor: '#ffffff',
            '&:hover': {
              color: '#1E293B',
              backgroundColor: '#F8FAFC',
            },
            '&.Mui-disabled': {
              color: '#CBD5E1',
              borderColor: '#E2E8F0',
            },
          },
        },
        sizeMedium: {
          lineHeight: '32px',
          fontSize: '16px',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          border: 'none',
        },
        inputSizeSmall: {
          height: '25px',
        },
        root: {
          background: '#ffffff',
          boxShadow: '0px 1px 2px 0px #1018280D',
          border: '1px solid #CBD5E1',
          '&.Mui-focused': {
            borderColor: '#c2d8ff',
            outline: '4px solid #ECF4FF',
          },
          '&:not(.Mui-focused):hover': {
            borderColor: '#CBD5E1',
            outline: '4px solid #ECF4FF',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        flexContainerVertical: {
          gap: '24px',
        },
        indicator: {
          display: 'none',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: '16px',
          lineHeight: '24px',
          color: '#64748B',
          borderRadius: '8px',
          padding: '9px 18px',
          fontWeight: 600,
          minHeight: '42px',
          textAlign: 'left',
          alignItems: 'start',
          border: '1px solid transparent',

          '&.Mui-selected': {
            background: '#ECF4FF',
            border: '1px solid #C2D8FF',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: '#64748B',
          fontWeight: 600,
          fontSize: '16px',
          lineHeight: '24px',
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          paddingBottom: '10px',
          paddingTop: '10px',

          '& .MuiDivider-root': {
            marginTop: '6px',
            marginBottom: '6px',
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '16px',
          color: '#334155',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          fontSize: '16px',
          color: '#475569',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: '14px',
          borderBottom: '1px solid #E2E8F0',
          padding: '20px 24px',
        },
        head: {
          color: '#475467',
          fontWeight: 500,
          fontSize: '12px',
          borderBottom: '1px solid #E2E8F0',
          padding: '12px 24px',
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#4B68FF',
            color: '#ffffff',
          },
        },
      },
    },
  },
  designTokens,
});

export { config, theme };
